import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function RecordViewerModal({ serverId, serviceUserId, collectionName, title, onClose }) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchRecords = async () => {
            setLoading(true);
            try {
                // Accessing: servers/{serverId}/serviceUsers/{serviceUserId}/{collectionName}
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, collectionName);
                const q = query(ref); // Getting all records for this sub-collection
                const snap = await getDocs(q);
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Sort by date if present
                loaded.sort((a, b) => {
                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.date || a.createdAt || a.timestamp);
                    const dateB = getMs(b.date || b.createdAt || b.timestamp);
                    return dateB - dateA;
                });
                
                setRecords(loaded);
            } catch (err) {
                console.error("Error fetching records:", err);
                setError("Failed to load records for this module.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId && collectionName) {
            fetchRecords();
        }
    }, [serverId, serviceUserId, collectionName]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>{title}</h2>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <p style={{ color: '#6b7280' }}>Loading records...</p>
                    ) : error ? (
                        <p style={{ color: '#ef4444' }}>{error}</p>
                    ) : records.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Records Found</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no {title.toLowerCase()} recorded for this service user yet.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {records.map((rec, index) => (
                                <div key={rec.id || index} style={styles.recordCard}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                        {/* Dynamically render all keys in the record for a generic view */}
                                        {Object.entries(rec).map(([key, value]) => {
                                            if (key === 'id' || typeof value === 'object') return null; // Skip IDs and complex objects in basic view
                                            return (
                                                <div key={key} style={{ minWidth: '150px', flex: '1' }}>
                                                    <div style={styles.fieldLabel}>{key.replace(/([A-Z])/g, ' $1').toUpperCase()}</div>
                                                    <div style={styles.fieldValue}>{String(value)}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '40px 0', textAlign: 'center' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.5px' },
    fieldValue: { fontSize: '14px', color: '#111827', wordBreak: 'break-word' }
};
